import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StackButton from "@/components/buttons/StackButton";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import StatusToggle from "@/components/toggles/StatusToggle";
import { UserAssignmentEditSkeleton } from "@/components/skeletons";
import { fetchAllGroups } from "@/services/group.service";
import { getUserById, updateUserById } from "@/services/user.service";
import { BUTTONS } from "@/utils/constants/appConstants/commonConstants";
import { ALL_GROUPS } from "@/utils/constants/constants";
import { setDataInLocalStorage } from "@/utils/functions/helperFunctions";

function UpdateUserGroups() {
    const [allGroups, setAllGroups] = useState<any[]>([]);
    const [userDetails, setUserDetails] = useState<any>({});
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [loader, setLoader] = useState(false);
    const params = useParams();
    const navigate = useNavigate();
    const id = params.id || "";

    useEffect(() => {
        let cancelled = false;

        setIsPageLoading(true);
        setAllGroups([]);

        getUserById(
            id,
            (response: any) => {
                if (cancelled) return;

                const user = response.data;
                setUserDetails(user);

                fetchAllGroups(
                    (groupsResponse: any) => {
                        if (cancelled) return;

                        setDataInLocalStorage(ALL_GROUPS, groupsResponse.data.groups);
                        const groupsList = groupsResponse.data.groups.map((group: any) => ({
                            ...group,
                            checked: user?.organizations?.[0]?.groups?.some(
                                (obj: { name: string }) => obj.name === group.name
                            )
                                ? true
                                : false,
                        }));
                        setAllGroups(groupsList);
                        setIsPageLoading(false);
                    },
                    (error: any) => {
                        if (cancelled) return;
                        console.log(error);
                        setIsPageLoading(false);
                    }
                );
            },
            (error: any) => {
                if (cancelled) return;
                console.log(error);
                setIsPageLoading(false);
            }
        );

        return () => {
            cancelled = true;
        };
    }, [id]);

    const onChangeGroup = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newGroupList = allGroups.map((group) => {
            if (e.target.name === group.name) {
                return { ...group, checked: e.target.checked };
            }
            return group;
        });
        setAllGroups(newGroupList);
    };

    const handleUpdateUser = () => {
        const newGroupList = allGroups.filter((group) => group.checked);
        const newGroupIdList = newGroupList.map((group) => group.id);

        const data = JSON.stringify({ groups: newGroupIdList });
        setLoader(true);
        updateUserById(
            id,
            data,
            (response: any) => {
                setLoader(false);
                navigate(-1);
            },
            (error: any) => {
                console.log(error);
                setLoader(false);
                navigate(-1);
            }
        );
    };

    if (isPageLoading) {
        return <UserAssignmentEditSkeleton sectionTitle="Groups" />;
    }

    return (
        <div className="gs-form-page">
            <div className="gs-form-page-header">
                <h3 className="gs-form-page-title min-w-0 truncate" id="edit-user-group-email-address-title">
                    {userDetails?.email_address}
                </h3>
                <StackButton
                    secondaryVariant="light"
                    primaryId="edit-user-group-save-btn"
                    secondaryId="edit-user-group-cancel-btn"
                    primaryButton={BUTTONS.SAVE}
                    secondaryButton={BUTTONS.CANCEL}
                    handlePrimary={handleUpdateUser}
                    handleSecondary={() => navigate(-1)}
                    isLoading={loader}
                />
            </div>

            <div className="gs-form-page-section">
                <h5 className="gs-form-section-title gs-form-section-title--spaced" id="edit-user-group-title">
                    Groups
                </h5>
                <div className="gs-list-page-card">
                    <ul className="gs-detail-list gs-form-toggle-list">
                        {allGroups.length > 0 ? (
                            allGroups.map((group, key) => (
                                <li className="gs-detail-list-item" key={group.id}>
                                    <StatusToggle
                                        index={key}
                                        onChange={onChangeGroup}
                                        name={group.name}
                                        id={group.id}
                                        checked={group.checked}
                                        disabled={group.name === "superadmin"}
                                    >
                                        <p className="m-0" id={`edit-user-group-name-label-${key}`}>
                                            {group.name}
                                        </p>
                                    </StatusToggle>
                                </li>
                            ))
                        ) : (
                            <li className="gs-detail-list-item">
                                <NoDataAvailable>
                                    <p className="m-0" id="edit-user-group-no-data">
                                        No groups available
                                    </p>
                                </NoDataAvailable>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
}

export default UpdateUserGroups;
